import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getAccessToken } from '@/utils/jwt';

export interface ConfigField {
  key: string;
  label: string;
  type: 'text' | 'password' | 'textarea' | 'number';
  required: boolean;
  placeholder?: string;
}

export interface DataLakeConnector {
  id: string;
  name: string;
  description: string;
  icon: string;
  config_fields: ConfigField[];
}

export interface RegisterConnectorRequest {
  connector_type: string;
  connector_id: string;
  config: Record<string, unknown>;
}

export interface DataSource {
  collection_id: string;
  name: string;
  description: string;
  source_type: string;
  is_active: boolean;
}

export const datalakeApi = createApi({
  reducerPath: 'datalakeApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['DataSource'],
  endpoints: (builder) => ({
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    listConnectorTypes: builder.query<DataLakeConnector[], void>({
      query: () => '/datalake/connectors',
      transformResponse: (response: { success: boolean; connectors: DataLakeConnector[] }) =>
        response.connectors,
    }),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    listActiveSources: builder.query<DataSource[], void>({
      query: () => '/datalake/sources',
      transformResponse: (response: { success: boolean; sources: DataSource[] }) =>
        response.sources,
      providesTags: ['DataSource'],
    }),
    registerConnector: builder.mutation<
      {
        success: boolean;
        connector: { connector_id: string; connector_type: string; enabled: boolean };
      },
      RegisterConnectorRequest
    >({
      query: (body) => ({ url: '/datalake/connectors', method: 'POST', body }),
      invalidatesTags: ['DataSource'],
    }),
    startGoogleDriveAuth: builder.query<
      { success: boolean; auth_url: string; connector_id: string },
      { folder_id?: string; connector_id?: string }
    >({
      query: (params) => ({
        url: '/datalake/google-drive/auth',
        params,
      }),
    }),
  }),
});

export const {
  useListConnectorTypesQuery,
  useListActiveSourcesQuery,
  useRegisterConnectorMutation,
  useLazyStartGoogleDriveAuthQuery,
} = datalakeApi;
